import { useState } from 'react';
import { ProcessEntity } from '@common/enums/processEntity';
import { imagePdfAndDocExtensions } from '@common/utils/fileExtensions';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import type { ICustomControlDiagnosticReportAddEditProps } from '../../interfaces/';
import { FilesUrlBase } from '../../../../../../common/enums/filesUrlBase';
import { ToastType } from '../../../../../../common/enums/toastType';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { ChangeServiceButton } from '../../../../elements/carePathButtons/ChangeServiceButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form } from '../../../../elements/common';
import { FormContextProvider } from '../../../../elements/formContext';
import { FormSection } from '../../../../elements/forms/formSection/FormSection';
import { RadioGroupField } from '../../../../fields/RadioGroup';
import { TextAreaField } from '../../../../fields/TextArea';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { AssetDropzoneWithActions } from '../../utils/v1/AssetDropzoneWithActions';
import { DiagnosisConclusionSelection } from '../../utils/v1/DiagnosisConclusionSelection';

export function GgzcgV1DiagnosticReportAddEdit(props: ICustomControlDiagnosticReportAddEditProps) {
    const [buttonDisabled, setButtonDisabled] = useState(false);

    return (
        <ProcessContainer>
            <ProcessItemHeader
                isFirstItem={props.isFirstItem}
                isItemOpen
                organizationName={props.currentDiagnosticReport?.performerOrganization?.name}
                practitionerName={
                    props.currentDiagnosticReport && props.currentDiagnosticReport.performerPractitioner
                        ? getDisplayName(props.currentDiagnosticReport.performerPractitioner)
                        : undefined
                }
                process={props.process}
                title={translateKey('diagnosticReport.category_consultReport')}
            />

            <Form onSubmit={() => null}>
                <FormContextProvider
                    value={{
                        placeholderGroupName: 'diagnosticReport',
                        item: props.diagnosticReport.customControlData,
                        errors: props.errors,
                        customControlData: true,
                        onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                        onDoValidate: async (key) => await props.validateOneCustomControl(key),
                    }}
                >
                    <FormSection isBorderLess>
                        <TextAreaField
                            automation="findings"
                            fieldKey="bevindingen"
                            isRequired
                            label={translateKey('diagnosticReport.findings')}
                            placeholder={translateKey('diagnosticReport.findings')}
                        />

                        <DiagnosisConclusionSelection
                            conclusie_diagnose={props.diagnosticReport.customControlData.conclusie_diagnose}
                            diagnosis={props.diagnosticReport.diagnosis}
                            errors={props.errors}
                            handleChangeConclusieDiagnose={props.handleChangeCustomControl}
                            handleChangeDiagnosisConsideration={async (diagnosis) =>
                                await props.handleChange('diagnosis', diagnosis)
                            }
                            isRequired
                            service={props.service}
                            validateConclusieDiagnose={props.validateOneCustomControl}
                            validateDiagnosisConsideration={async () => props.validateOne('diagnosis')}
                        />

                        <TextAreaField
                            automation="policy"
                            fieldKey="beleid"
                            isRequired
                            label={translateKey('diagnosticReport.policy')}
                            placeholder={translateKey('diagnosticReport.policy')}
                        />

                        <RadioGroupField
                            automation="sendToSpecialist"
                            error={props.errors.get('customControlData.bezoek_aan_specialist_nodig')}
                            fieldKey="bezoek_aan_specialist_nodig"
                            isRequired
                            label={translateKey('diagnosticReport.visitToSpecialistNecessary')}
                            name="bezoek_aan_specialist_nodig"
                            options={['Ja', 'Nee', 'Zie beleid']}
                        />

                        <TextAreaField
                            automation="remarks"
                            fieldKey="toelichting"
                            label={translateKey('diagnosticReport.remarks')}
                            placeholder={translateKey('diagnosticReport.remarks')}
                        />
                    </FormSection>
                    <FormSection
                        isBorderLess
                        title={translateKey('diagnosticReport.attachment')}
                    >
                        <AssetDropzoneWithActions
                            acceptedFiles={imagePdfAndDocExtensions}
                            additionalFilesCheckboxText={translateKey('diagnosticReport.addAttachment')}
                            assets={props.diagnosticReport.assets}
                            assetsBaseUrl={FilesUrlBase.diagnosticReports}
                            cleanDropzoneAfterCompletingFiles={props.isAutoSaveActive}
                            onChange={async (assets) => await props.handleChange('assets', assets)}
                            onUploading={async (isUploading) => setButtonDisabled(isUploading)}
                            processActivityId={props.diagnosticReport.id}
                            service={props.service}
                            showAdditionalFilesCheckbox
                        />
                    </FormSection>
                </FormContextProvider>

                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={props.onCancel}
                            entity={ProcessEntity.diagnosticReport}
                            isExisting={!!props.request.id}
                            revertChanges={props.revertDiagnosticReportChanges}
                            serviceCode={props.service.code}
                            serviceType={props.service.serviceType}
                        />,
                        <ChangeServiceButton
                            reloadProcess={props.reloadProcess}
                            request={props.request}
                            saveDiagnosticReport={async () => {
                                await save('draft');
                                setButtonDisabled(false);
                            }}
                        />,
                        <SaveButton
                            buttonDisabled={buttonDisabled}
                            save={async () => {
                                await saveDiagnosticReport('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={buttonDisabled}
                            saveSend={async () => {
                                await saveDiagnosticReport('final');
                            }}
                        />,
                    ]}
                />
            </Form>
        </ProcessContainer>
    );

    async function save(saveType: 'draft' | 'final') {
        setButtonDisabled(true);
        const validatedDiagnosticReport = await props.validateDiagnosticReport(saveType);
        if (!validatedDiagnosticReport) {
            setButtonDisabled(false);

            return;
        }

        return await props.onSaveDiagnosticReport(saveType);
    }

    async function saveDiagnosticReport(saveType: 'draft' | 'final') {
        setButtonDisabled(true);

        const diagnosticReportId = await save(saveType);
        if (!diagnosticReportId) {
            return;
        }

        if (saveType === 'draft') {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'diagnosticReport',
                translateKey('toast.draftDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'diagnosticReport',
                translateKey('toast.answerSentToOrganizationSuccess', props.request.requesterOrganization?.name)
            );
        }

        setButtonDisabled(false);
        await props.onSave(diagnosticReportId);
    }
}

export function ggzcgV1DiagnosticReportInitializeCustomControlData() {
    return {
        bevindingen: null,
        conclusie_diagnose: null,
        beleid: null,
        bezoek_aan_specialist_nodig: null,
        toelichting: null,
    };
}
